import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { Icon } from "../components/Icon";
import { PageTitle } from "../components/PageTitle";
import { Button, Drawer, EmptyState, FieldLabel, Input, Select } from "../components/ui";
import { Phase3ProgressDock } from "../components/Phase3ProgressDock";
import { Phase3SynthesisProgress } from "../components/Phase3SynthesisProgress";
import { CostAcknowledgementGate } from "../components/CostAcknowledgementGate";
import { DeskTargetGate } from "../components/DeskTargetGate";
import { LeadBulkToolbar } from "../components/LeadBulkToolbar";
import { SynthesisPromptEditor } from "../components/SynthesisPromptEditor";
import { TargetJobRunsPanel } from "../components/TargetJobRunsPanel";
import { useToast } from "../components/Toast";
import { useDeskSpend } from "../context/DeskSpendContext";
import { useDraftState } from "../context/DraftStateContext";
import {
  loadDeskPreferences,
  savePdrSchemaProfile,
  savePhase3EnablePdrReasoning,
} from "../lib/deskPreferences";
import {
  ApiError,
  apiGet,
  apiPost,
  apiPut,
  closeGap,
  clusterSpend,
  decidePossibleDuplicate,
  listClusterChunkIssues,
  listClusterMentions,
  listClusterUnresolved,
  leadProjectSummaries,
  listGaps,
  listProjectParticipants,
  listReconciliationDecisions,
  reopenGap,
  researchGap,
  resolveUnresolvedRouting,
} from "../lib/api";
import { resolveJobForDeskTarget } from "../lib/deskTarget";
import { hasGeneratedLeads, isFoundViaLead, matchesLeadFilter, type LeadFilter } from "../lib/leads";
import type { Phase1JobPoll } from "../lib/phase1Job";
import type { Phase3Progress } from "../lib/phase3Progress";
import {
  normalizePdrProfile,
  PDR_PROFILE_OPTIONS,
  type PdrSchemaProfile,
} from "../lib/pdrFields";
import type {
  ClusterSpendSummary,
  EvidenceChunkIssue,
  GapSearchItem,
  InitiativeMention,
  LeadProjectSummary,
  Phase3Bucket,
  Phase3State,
  PossibleDuplicateMatch,
  ProjectParticipant,
  ReconciliationDecision,
  ResearchGap,
  UnresolvedRouting,
} from "../lib/types";
import {
  fetchSynthesisPrompts,
  type SynthesisPromptTemplate,
} from "../lib/synthesisPromptsApi";
import { formatApiError, getStoredJobId } from "../lib/utils";

function bucketContractor(b: Phase3Bucket): string {
  return (
    b.main_contractor ||
    b.contractor_display ||
    b.vendor_display_name ||
    b.bucket_id ||
    "—"
  );
}

function bucketTarget(b: Phase3Bucket): string {
  return b.target_display_name || b.target_company || "—";
}

/** Select value for "show every target's vendor clusters". */
const ALL_TARGETS = "__all__";

/** A cluster's projects, as the server grouped them by evidence lineage. */
function bucketProjectIds(b: Phase3Bucket): string[] {
  return Array.isArray(b.project_ids) ? b.project_ids.filter(Boolean) : [];
}

/** Leads are generated from projects: a cluster with none has nothing to work from. */
function leadEligible(b: Phase3Bucket): boolean {
  return bucketProjectIds(b).length > 0;
}

const CLUSTER_UNIT = { one: "cluster", many: "clusters" };

type ClusterLeadFilter = "all" | "needs" | "none" | "stale" | "done";
type ClusterOriginFilter = "all" | "original" | "second" | "has_lead";

const CLUSTER_ORIGIN_FILTERS: { id: ClusterOriginFilter; label: string }[] = [
  { id: "all", label: "Origin: all clusters" },
  { id: "original", label: "Origin: initial search only" },
  { id: "second", label: "Origin: second iteration (all sources from lead research)" },
  { id: "has_lead", label: "Origin: with sources from lead research" },
];

function originMatches(b: Phase3Bucket, filter: ClusterOriginFilter): boolean {
  const origin = b.source_origin || "";
  if (filter === "all") return true;
  if (filter === "original") return origin === "original";
  if (filter === "second") return origin === "lead_research";
  return origin === "lead_research" || origin === "mixed";
}

const CLUSTER_LEAD_FILTERS: { id: ClusterLeadFilter; label: string }[] = [
  { id: "all", label: "Leads: all clusters" },
  { id: "needs", label: "Leads: clusters with projects needing leads" },
  { id: "none", label: "Leads: clusters with projects without leads" },
  { id: "stale", label: "Leads: clusters with out-of-date leads" },
  { id: "done", label: "Leads: clusters where every project has current leads" },
];

function targetQueryForApi(targetFilter: string): string | undefined {
  if (targetFilter === ALL_TARGETS) return "all";
  if (targetFilter) return targetFilter;
  return undefined;
}

function scopeLabel(targetFilter: string, jobTarget: string): string {
  if (targetFilter === ALL_TARGETS) return "All targets";
  const name = targetFilter || jobTarget;
  return name && name !== "—" ? name : "All targets";
}

function selectTargetValue(targetFilter: string, jobTarget: string): string {
  if (targetFilter === ALL_TARGETS) return ALL_TARGETS;
  return targetFilter || jobTarget || ALL_TARGETS;
}

function bucketStatusLabel(b: Phase3Bucket): string | null {
  const pending = Number(b.pending_source_count ?? b.pending_count ?? 0);
  const total = Number(b.evidence_count ?? 0);
  if (total === 0 || b.synthesis_inbox_status === "awaiting_extraction") {
    return "Awaiting extraction";
  }
  if (pending > 0) {
    return pending === 1 ? "1 new source" : `${pending} new sources`;
  }
  return "Up to date";
}

type TrackedRun = {
  runId: string;
  bucketId: string;
  targetName: string;
  contractorName: string;
  status: string;
  progress: Phase3Progress | null;
  log: Phase1JobPoll["log"] | null;
  error: string;
};

/* ========================================================================== *
 * Project discovery v3 — cluster review sections (plan section 5.10).
 *
 * Everything below hangs off ONE selected cluster and reads only through the
 * typed helpers in lib/api.ts.  The routing review queue is eager because the
 * backend has been writing `routing_unresolved` rows since M0 and nothing has
 * ever rendered them: evidence held for review must be visible without the
 * operator knowing to go looking.  The rest load on first expand, so opening a
 * cluster is still one request until the operator asks for more.
 *
 * With every v3 flag off these reads return empty lists (the v3 tables are
 * never written on the legacy path) and each section says so in its own words
 * rather than showing a blank box.
 * ========================================================================== */

type Tone = "neutral" | "primary" | "warn" | "error" | "ok";

const TONE_CLASS: Record<Tone, string> = {
  neutral: "bg-surface-container-high text-on-surface-variant border-outline-variant",
  primary: "bg-primary/10 text-primary border-primary/30",
  warn: "bg-tertiary/10 text-tertiary border-tertiary/30",
  error: "bg-error/10 text-error border-error/30",
  ok: "bg-secondary/10 text-secondary border-secondary/30",
};

function Badge({
  tone = "neutral",
  title,
  children,
}: {
  tone?: Tone;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={`shrink-0 text-[9px] font-bold px-2 py-0.5 rounded border uppercase tracking-wide ${TONE_CLASS[tone]}`}
    >
      {children}
    </span>
  );
}

function fmtTime(value: string | undefined | null): string {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString();
}

function fmtUsd(value: number | null | undefined): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "$0.00";
  return `$${n.toFixed(n && Math.abs(n) < 0.01 ? 4 : 2)}`;
}

function SourceLink({ url, title }: { url: string; title: string }) {
  const label = title || url || "Untitled source";
  if (!url) {
    return <span className="text-body-sm text-on-surface font-semibold">{label}</span>;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="text-body-sm text-primary font-semibold hover:underline break-words"
      title={url}
    >
      {label}
    </a>
  );
}

/** Error surface for a section: never silently blank when the API fails. */
function SectionError({
  message,
  onRetry,
  note,
}: {
  message: string;
  onRetry: () => void;
  note?: string;
}) {
  return (
    <div className="mb-3 border border-error/40 bg-error/10 p-3 flex items-start gap-2 rounded">
      <Icon name="error" className="text-error text-base shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-body-sm text-error break-words">{message}</p>
        {note ? <p className="text-[10px] text-on-surface-variant mt-1">{note}</p> : null}
      </div>
      <Button size="sm" variant="outline" onClick={onRetry} className="shrink-0">
        Retry
      </Button>
    </div>
  );
}

function SectionLoading({ label }: { label: string }) {
  return (
    <p className="flex items-center gap-2 text-body-sm text-on-surface-variant">
      <span className="orion-spinner" aria-hidden />
      {label}
    </p>
  );
}

function SectionEmpty({ text }: { text: string }) {
  return (
    <p className="text-body-sm text-on-surface-variant leading-relaxed border border-outline-variant/60 bg-surface-container-lowest p-3 rounded">
      {text}
    </p>
  );
}

type Loadable<T> = {
  data: T | null;
  loading: boolean;
  error: string;
  loaded: boolean;
  reload: () => void;
};

/**
 * One fetch per (key, active) pair, re-run on `reload()`.
 *
 * `active` is what makes a collapsed section cost nothing: the effect returns
 * before fetching until the operator opens it.  The loader is held in a ref so
 * an inline arrow closure does not re-trigger the effect on every render.
 */
function useLoadable<T>(key: string, active: boolean, load: () => Promise<T>): Loadable<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [token, setToken] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!active || !key) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    loadRef
      .current()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(formatApiError(e));
        setLoaded(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, active, token]);

  const reload = useCallback(() => setToken((t) => t + 1), []);
  return { data, loading, error, loaded, reload };
}

/** Header line: a count once loaded, an honest "not loaded yet" before that. */
function sectionMeta(loadable: Loadable<unknown>, describe: () => string): string {
  if (loadable.loading) return "Loading…";
  if (loadable.error) return "Failed to load";
  if (!loadable.loaded) return "Expand to load";
  return describe();
}

/* --------------------------------------------------- routing review queue -- */

const UNRESOLVED_REASONS: Record<string, { label: string; help: string }> = {
  omitted_by_router: {
    label: "Omitted by router",
    help: "The router returned no decision for this source, so its facts were never attached to a project.",
  },
  unknown_project_id: {
    label: "Unknown project id",
    help: "The router routed to a project id that does not exist for this target.",
  },
  identity_conflict_slug: {
    label: "Identity conflict",
    help: "The proposed new project collides with an existing project slug — routing it would have overwritten that project.",
  },
  new_project_missing_name: {
    label: "New project without a name",
    help: "The router asked for a new project but gave no name to create it under.",
  },
  possible_duplicate: {
    label: "Possible duplicate",
    help: "Synthesis proposed a new project on a page a known project shares, and the same-page check found it is that project, so nothing was created. Same project: the known project takes the page and the name. Separate project: it is created. Either way the next synthesis run reads the page again.",
  },
  other_vendor_project: {
    label: "Another vendor's project",
    help: "The page describes a project another vendor's cluster owns, so this cluster could not write it. Whether this contractor takes part is a participation decision.",
  },
};

function duplicateMatches(row: UnresolvedRouting): PossibleDuplicateMatch[] {
  const payload = row.payload || {};
  const listed = Array.isArray(payload.matches) ? (payload.matches as PossibleDuplicateMatch[]) : [];
  if (listed.length) return listed;
  return payload.project_id ? [payload as unknown as PossibleDuplicateMatch] : [];
}

function PossibleDuplicateMatches({ row }: { row: UnresolvedRouting }) {
  return (
    <ul className="space-y-2 border-t border-outline-variant/50 pt-2">
      {duplicateMatches(row).map((match, index) => (
        <li key={`${match.project_id}-${index}`} className="text-[11px] leading-relaxed space-y-0.5">
          <p className="text-on-surface">
            Proposed <strong>“{match.proposed_project_name}”</strong> looks like{" "}
            <Link
              to={`/projects?project=${encodeURIComponent(match.project_id)}`}
              className="text-primary font-semibold hover:underline"
            >
              {match.project_name || match.project_id}
            </Link>
            {match.confidence ? (
              <span className="text-on-surface-variant"> · {match.confidence} confidence</span>
            ) : null}
          </p>
          {match.proposed_summary ? (
            <p className="text-on-surface-variant">{match.proposed_summary}</p>
          ) : null}
          {match.reasoning ? (
            <p className="text-on-surface-variant italic">Check: {match.reasoning}</p>
          ) : null}
          {(match.evidence_urls || []).length > 1 ? (
            <p className="text-on-surface-variant">
              Read from {(match.evidence_urls || []).length} pages; a decision re-reads all of them.
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function UnresolvedRow({
  row,
  busy,
  onResolve,
  onDecide,
}: {
  row: UnresolvedRouting;
  busy: boolean;
  onResolve: (row: UnresolvedRouting, status: string) => void;
  onDecide: (row: UnresolvedRouting, verdict: "same" | "separate") => void;
}) {
  const reason = UNRESOLVED_REASONS[row.reason];
  const duplicate = row.reason === "possible_duplicate";
  const payloadEntries = Object.entries(row.payload || {}).filter(
    ([, value]) => value !== null && value !== "" && value !== undefined,
  );
  return (
    <li className="border border-outline-variant bg-surface-container-low p-3 rounded space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge tone="error" title={reason?.help}>
              {reason?.label || row.reason || "Held"}
            </Badge>
            <Badge tone="neutral">{row.status || "open"}</Badge>
            <span className="text-[10px] font-data-mono text-on-surface-variant">
              held {fmtTime(row.created_at)}
            </span>
          </div>
          <SourceLink url={row.url} title={row.title} />
          {reason ? (
            <p className="text-[11px] text-on-surface-variant leading-relaxed">{reason.help}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {duplicate ? (
            <>
              <Button
                size="sm"
                variant="outline-primary"
                disabled={busy}
                onClick={() => onDecide(row, "same")}
                title="The proposal is the known project: it takes the page and the proposed name"
              >
                {busy ? <span className="orion-spinner" aria-hidden /> : null}
                Same project
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                disabled={busy}
                onClick={() => onDecide(row, "separate")}
                title="Create the proposed project; the next synthesis run writes its record"
              >
                Separate project
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="outline-primary"
              disabled={busy}
              onClick={() => onResolve(row, "resolved")}
              title="Clear this hold — the source has been dealt with"
            >
              {busy ? <span className="orion-spinner" aria-hidden /> : null}
              Resolve
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => onResolve(row, "dismissed")}
            title="Drop this hold without acting on the source"
          >
            Dismiss
          </Button>
        </div>
      </div>
      {duplicate ? (
        <PossibleDuplicateMatches row={row} />
      ) : payloadEntries.length ? (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 font-data-mono text-[10px] text-on-surface-variant border-t border-outline-variant/50 pt-2">
          {payloadEntries.slice(0, 8).map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="text-outline uppercase">{key}</dt>
              <dd className="break-words text-on-surface-variant">
                {typeof value === "string" ? value : JSON.stringify(value)}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </li>
  );
}

/* ------------------------------------------------------------- mentions --- */

const MENTION_STATUS_OPTIONS = [
  { id: "", label: "All statuses" },
  { id: "pending", label: "Pending" },
  { id: "existing", label: "Matched to existing" },
  { id: "new", label: "New project" },
  { id: "insufficient", label: "Insufficient evidence" },
  { id: "no_project", label: "No project" },
  { id: "resolved_no_new_facts", label: "Resolved — no new facts" },
];

function mentionTitle(mention: InitiativeMention): string {
  const fields = mention.fields || {};
  const names = fields.program_names;
  if (Array.isArray(names) && names.length) return String(names[0] || "").trim() || "Unnamed initiative";
  if (typeof names === "string" && names.trim()) return names.trim();
  const objective = fields.objective;
  if (typeof objective === "string" && objective.trim()) return objective.trim();
  return "Unnamed initiative";
}

function mentionStatusTone(status: string): Tone {
  if (status === "pending") return "warn";
  if (status === "existing" || status === "new") return "ok";
  if (status === "insufficient" || status === "no_project") return "neutral";
  return "primary";
}

function MentionRow({ mention }: { mention: InitiativeMention }) {
  const fields = mention.fields || {};
  const detail = (["objective", "scope", "business_unit", "geography"] as const)
    .map((key) => {
      const value = fields[key];
      if (Array.isArray(value)) return value.join(", ");
      return typeof value === "string" ? value : "";
    })
    .find((value) => value && value.trim());
  return (
    <li className="border border-outline-variant bg-surface-container-low p-3 rounded space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="text-body-sm font-semibold text-on-surface break-words">
            {mentionTitle(mention)}
          </p>
          <SourceLink url={mention.url} title={mention.title} />
          {detail ? (
            <p className="text-[11px] text-on-surface-variant leading-relaxed line-clamp-3">
              {detail}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Badge tone={mentionStatusTone(mention.resolution_status)}>
            {mention.resolution_status || "unknown"}
          </Badge>
          {mention.grounded ? (
            <Badge tone="ok" title="A verbatim passage was found in the referenced chunk">
              grounded
            </Badge>
          ) : (
            <Badge tone="error" title="No verbatim passage was found — the model may not have read this">
              ungrounded
            </Badge>
          )}
          {!mention.has_defined_initiative ? (
            <Badge tone="neutral" title="Relationship evidence only — no identifiable initiative">
              relationship only
            </Badge>
          ) : null}
        </div>
      </div>
      {mention.vendors?.length ? (
        <ul className="flex flex-wrap gap-1.5 border-t border-outline-variant/50 pt-2">
          {mention.vendors.map((vendor) => (
            <li
              key={`${mention.mention_id}:${vendor.vendor_id}:${vendor.raw_name}`}
              className="text-[10px] font-data-mono px-2 py-0.5 rounded border border-outline-variant text-on-surface-variant"
              title={vendor.uncertainty ? "Role is uncertain in the evidence" : undefined}
            >
              {vendor.vendor_name || vendor.raw_name || "unknown vendor"}
              <span className="text-primary"> · {vendor.role || "mentioned"}</span>
              {vendor.uncertainty ? <span className="text-tertiary"> ?</span> : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[10px] text-on-surface-variant border-t border-outline-variant/50 pt-2">
          No organization named in a delivery role by this mention.
        </p>
      )}
      {mention.passage ? (
        <p className="text-[10px] font-data-mono text-on-surface-variant border-l-2 border-outline-variant pl-2 leading-relaxed line-clamp-3">
          “{mention.passage}”
        </p>
      ) : null}
      <p className="text-[10px] font-data-mono text-outline">
        chunk {mention.chunk_index} · v{mention.version_no} · {fmtTime(mention.created_at)}
        {mention.project_id ? ` · project ${mention.project_id.slice(0, 8)}` : ""}
      </p>
    </li>
  );
}

/* ------------------------------------------------------------------ gaps --- */

function gapTone(gap: ResearchGap): Tone {
  if (gap.open) return "warn";
  if (gap.status === "resolved") return "ok";
  return "neutral";
}

function GapRow({
  gap,
  flagOn,
  busy,
  result,
  error,
  onResearch,
  onClose,
  onReopen,
}: {
  gap: ResearchGap;
  flagOn: boolean;
  busy: boolean;
  result: { queries: number; allowanceUsd: number; items: GapSearchItem[] } | undefined;
  error: string;
  onResearch: (gap: ResearchGap) => void;
  onClose: (gap: ResearchGap, status: string) => void;
  onReopen: (gap: ResearchGap) => void;
}) {
  const cooldown = String(gap.cooldown_until || "").trim();
  const cooling = Boolean(cooldown) && new Date(cooldown).getTime() > Date.now();
  const researchTitle = !flagOn
    ? "ORION1_GAP_RESEARCH is off — the planner refuses this with a 409"
    : !gap.open
      ? "Gap is closed — reopen it to grant a fresh attempt budget"
      : cooling
        ? `Cooling down until ${fmtTime(cooldown)}`
        : "Plan bounded follow-up queries — nothing is executed now, they go to the next search run";
  return (
    <li className="border border-outline-variant bg-surface-container-low p-3 rounded space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge tone={gapTone(gap)}>{gap.status || "open"}</Badge>
            <Badge tone="primary">{gap.gap_type || "gap"}</Badge>
            <span className="text-[10px] font-data-mono text-on-surface-variant">
              priority {gap.priority}
            </span>
          </div>
          <p className="text-body-sm text-on-surface break-words">{gap.question || "—"}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            size="sm"
            variant="outline-primary"
            disabled={busy || !flagOn || !gap.open || cooling}
            onClick={() => onResearch(gap)}
            title={researchTitle}
          >
            {busy ? <span className="orion-spinner" aria-hidden /> : null}
            Research this gap
          </Button>
          {gap.open ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => onClose(gap, "answered")}
              title="Close this gap as answered"
            >
              Close
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => onReopen(gap)}
              title="Grant a fresh attempt budget"
            >
              Reopen
            </Button>
          )}
        </div>
      </div>
      <p className="text-[10px] font-data-mono text-on-surface-variant border-t border-outline-variant/50 pt-2">
        attempts {gap.attempts} · allowance {fmtUsd(gap.allowance_usd)}
        {cooldown ? ` · cooldown ${cooling ? "until" : "expired"} ${fmtTime(cooldown)}` : " · no cooldown"}
        {gap.stop_reason ? ` · stopped: ${gap.stop_reason}` : ""}
      </p>
      {error ? <p className="text-[11px] text-error break-words">{error}</p> : null}
      {result ? (
        <div className="border border-primary/30 bg-primary/5 p-2 rounded space-y-1">
          <p className="text-[11px] text-primary">
            {result.queries} quer{result.queries === 1 ? "y" : "ies"} planned against{" "}
            {fmtUsd(result.allowanceUsd)} of run allowance. Nothing has run yet — they execute in
            the next search run.
          </p>
          <ul className="font-data-mono text-[10px] text-on-surface-variant space-y-0.5">
            {result.items.slice(0, 8).map((item) => (
              <li key={item.id} className="break-words">
                {item.engine_label || item.engine || "search"} · {item.query}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </li>
  );
}

/* -------------------------------------------------------- decision log ----- */

function DecisionRow({ decision }: { decision: ReconciliationDecision }) {
  return (
    <li className="border border-outline-variant bg-surface-container-low p-3 rounded space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge tone={decision.outcome === "existing" || decision.outcome === "new" ? "ok" : "neutral"}>
          {decision.outcome || "—"}
        </Badge>
        <Badge tone="primary">{decision.method || "unknown method"}</Badge>
        {decision.confidence ? <Badge tone="neutral">{decision.confidence}</Badge> : null}
        {decision.operator_override ? <Badge tone="warn">operator override</Badge> : null}
        {decision.valid ? null : (
          <Badge tone="error" title="Superseded by a later decision or reopened">
            superseded
          </Badge>
        )}
        <span className="text-[10px] font-data-mono text-on-surface-variant ml-auto">
          {fmtTime(decision.created_at)}
        </span>
      </div>
      {decision.reasoning ? (
        <p className="text-[11px] text-on-surface-variant leading-relaxed break-words">
          {decision.reasoning}
        </p>
      ) : null}
      {decision.passage ? (
        <p className="text-[10px] font-data-mono text-on-surface-variant border-l-2 border-outline-variant pl-2 line-clamp-2">
          “{decision.passage}”
        </p>
      ) : null}
      <p className="text-[10px] font-data-mono text-outline break-words">
        mention {decision.mention_id ? decision.mention_id.slice(0, 8) : "—"}
        {decision.mention_status ? ` (${decision.mention_status})` : ""}
        {decision.project_id ? ` · project ${decision.project_id.slice(0, 8)}` : ""}
        {decision.model ? ` · ${decision.model}` : ""}
        {decision.candidate_ids?.length ? ` · ${decision.candidate_ids.length} candidate(s)` : ""}
      </p>
    </li>
  );
}

/* ------------------------------------------------------------------ spend -- */

function ClusterSpendCard({ summary, targetName }: { summary: ClusterSpendSummary; targetName: string }) {
  const targetScoped = summary.scope !== "cluster";
  const operations = Object.entries(summary.operations || {});
  return (
    <div className="space-y-3">
      {targetScoped ? (
        <div className="border border-tertiary/40 bg-tertiary/10 p-3 rounded flex items-start gap-2">
          <Icon name="warning" className="text-tertiary text-base shrink-0" />
          <div className="min-w-0">
            <p className="text-body-sm text-tertiary font-semibold">
              Target-level spend — not attributable to this cluster
            </p>
            <p className="text-[11px] text-on-surface-variant mt-1 leading-relaxed">
              No ledger event names this cluster, so this is the total for{" "}
              {summary.target_display_name || targetName || "the target"}. Do not read it as what
              this cluster cost.
              {summary.note ? ` ${summary.note}` : ""}
            </p>
          </div>
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <div className="border border-outline-variant bg-surface-container-low p-3 rounded">
          <p className="text-[10px] uppercase tracking-wide text-on-surface-variant">
            {targetScoped ? "Target spend" : "Cluster spend"}
          </p>
          <p className="font-data-mono text-headline-md text-on-surface mt-1">
            {fmtUsd(summary.cost_usd)}
          </p>
        </div>
        <div className="border border-outline-variant bg-surface-container-low p-3 rounded">
          <p className="text-[10px] uppercase tracking-wide text-on-surface-variant">
            Billed calls
          </p>
          <p className="font-data-mono text-headline-md text-on-surface mt-1">{summary.calls}</p>
        </div>
      </div>
      {operations.length ? (
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-[11px] font-data-mono">
            <thead>
              <tr className="text-left text-outline uppercase text-[9px]">
                <th className="py-1 pr-3">Operation</th>
                <th className="py-1 pr-3 text-right">Calls</th>
                <th className="py-1 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {operations.map(([name, totals]) => (
                <tr key={name} className="border-t border-outline-variant/50">
                  <td className="py-1 pr-3 text-on-surface-variant break-words">{name}</td>
                  <td className="py-1 pr-3 text-right text-on-surface-variant">{totals.calls}</td>
                  <td className="py-1 text-right text-on-surface">{fmtUsd(totals.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <SectionEmpty text="No billed operations recorded yet for this scope." />
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- chunks -- */

const CHUNK_STATUS_HELP: Record<string, string> = {
  deferred_budget: "Held back by a stage cap or the run allowance — evidence bought and not read.",
  failed: "Extraction errored on this chunk.",
  skipped_reason: "Skipped deliberately; the reason is recorded below.",
};

function ChunkRow({ chunk }: { chunk: EvidenceChunkIssue }) {
  return (
    <li className="border border-outline-variant bg-surface-container-low p-3 rounded space-y-1.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <SourceLink url={chunk.url} title={chunk.title} />
          <p className="text-[10px] font-data-mono text-on-surface-variant">
            chunk {chunk.chunk_index} · v{chunk.version_no} · attempts {chunk.attempts} · signal{" "}
            {chunk.signal_score.toFixed(2)}
            {chunk.model ? ` · ${chunk.model}` : ""}
          </p>
        </div>
        <Badge
          tone={chunk.status === "failed" ? "error" : "warn"}
          title={CHUNK_STATUS_HELP[chunk.status]}
        >
          {chunk.status}
        </Badge>
      </div>
      {chunk.deferred_reason || chunk.error ? (
        <p className="text-[11px] text-on-surface-variant break-words">
          {chunk.deferred_reason || chunk.error}
        </p>
      ) : null}
      <p className="text-[10px] font-data-mono text-outline">{fmtTime(chunk.updated_at)}</p>
    </li>
  );
}

/* ------------------------------------------------- projects: participation -- */

function ProjectParticipationRow({
  projectId,
  projectName,
  foundViaLead,
}: {
  projectId: string;
  projectName: string;
  /** Set when a lead's research found this project (the second iteration). */
  foundViaLead?: { lead: string; originProject: string } | null;
}) {
  const [open, setOpen] = useState(false);
  const participants = useLoadable(projectId, open, () => listProjectParticipants(projectId));
  const rows = participants.data?.participants || [];
  const delivering = new Set(
    (participants.data?.delivering_participants || []).map((p) => `${p.vendor_name}:${p.role}`),
  );
  return (
    <li className="panel-border p-3 bg-surface-container-low space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-primary font-semibold text-body-sm min-w-0 break-words">
          {projectName || projectId}
          {foundViaLead ? (
            <span
              className="ml-2 align-middle text-[9px] font-bold px-2 py-0.5 rounded border uppercase bg-tertiary/10 text-tertiary border-tertiary/30"
              title={`Found by the research of lead "${foundViaLead.lead || "?"}"${foundViaLead.originProject ? ` from ${foundViaLead.originProject}` : ""}`}
            >
              found via lead
            </span>
          ) : null}
        </span>
        <div className="flex items-center gap-3 shrink-0">
          <Button
            variant="link-muted"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            title="Roles and participation recorded for this project"
          >
            {open ? "Hide participation" : "Participation"}
          </Button>
          {projectId ? (
            <Link
              to={`/projects?project=${encodeURIComponent(projectId)}`}
              className="text-[10px] uppercase text-primary hover:underline shrink-0"
            >
              Detail
            </Link>
          ) : null}
        </div>
      </div>
      {open ? (
        <div className="border-t border-outline-variant/50 pt-2">
          {participants.loading ? (
            <SectionLoading label="Loading participation…" />
          ) : participants.error ? (
            <SectionError message={participants.error} onRetry={participants.reload} />
          ) : rows.length ? (
            <ul className="flex flex-wrap gap-1.5">
              {rows.map((participant: ProjectParticipant, index: number) => (
                <li
                  key={`${participant.vendor_name}:${participant.role}:${index}`}
                  className={`text-[10px] font-data-mono px-2 py-0.5 rounded border ${
                    delivering.has(`${participant.vendor_name}:${participant.role}`)
                      ? "border-primary/40 text-primary"
                      : "border-outline-variant text-on-surface-variant"
                  }`}
                  title={
                    participant.uncertainty
                      ? "Role is uncertain in the evidence"
                      : `Decided in cluster ${participant.cluster_id || "—"}`
                  }
                >
                  {participant.vendor_name || "unknown vendor"} · {participant.role || "mentioned"}
                  {participant.uncertainty ? " ?" : ""}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[11px] text-on-surface-variant">
              No participation rows. A project written by the legacy path has none — its contractor
              lives in the PDR, not in <span className="font-data-mono">project_participations</span>.
            </p>
          )}
        </div>
      ) : null}
    </li>
  );
}

/* --------------------------------------------------------- the workbench --- */

function ClusterWorkbench({
  clusterId,
  targetName,
  seedUnresolved,
}: {
  clusterId: string;
  targetName: string;
  seedUnresolved: UnresolvedRouting[];
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState<Record<string, boolean>>({
    unresolved: true,
    mentions: false,
    gaps: false,
    decisions: false,
    spend: false,
    chunks: false,
  });
  const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ---- unresolved routing: eager, because nothing else shows these rows --- */
  const unresolved = useLoadable(clusterId, true, () => listClusterUnresolved(clusterId));
  const [clearedHolds, setClearedHolds] = useState<Record<string, string>>({});
  const [holdBusy, setHoldBusy] = useState("");
  const holdRows = useMemo(() => {
    const live = unresolved.data?.unresolved;
    const rows = live ?? seedUnresolved;
    return rows.filter((row) => !clearedHolds[row.unresolved_id]);
  }, [unresolved.data, seedUnresolved, clearedHolds]);
  const clearedCount = Object.keys(clearedHolds).length;

  const resolveHold = async (row: UnresolvedRouting, status: string) => {
    if (holdBusy) return;
    setHoldBusy(row.unresolved_id);
    try {
      await resolveUnresolvedRouting(row.unresolved_id, { status });
      setClearedHolds((prev) => ({ ...prev, [row.unresolved_id]: status }));
      toast(`Routing hold ${status}`);
      // The optimistic removal keeps the list steady; the refetch is what makes
      // the count honest if somebody else has queued or cleared a hold since.
      unresolved.reload();
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setHoldBusy("");
    }
  };

  const decideHold = async (row: UnresolvedRouting, verdict: "same" | "separate") => {
    if (holdBusy) return;
    setHoldBusy(row.unresolved_id);
    try {
      const { unresolved: decided } = await decidePossibleDuplicate(row.unresolved_id, verdict);
      setClearedHolds((prev) => ({ ...prev, [row.unresolved_id]: decided.status }));
      const pages = decided.pages.length;
      toast(
        verdict === "same"
          ? `Kept as the known project. The next synthesis run re-reads ${pages} page${pages === 1 ? "" : "s"} for it.`
          : `Created ${decided.created_project_ids.length} project${decided.created_project_ids.length === 1 ? "" : "s"}. The next synthesis run writes the record.`,
      );
      unresolved.reload();
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setHoldBusy("");
    }
  };

  /* ---------------------------------------------------------- mentions ---- */
  const [mentionStatus, setMentionStatus] = useState("");
  const mentions = useLoadable(`${clusterId}::${mentionStatus}`, open.mentions, () =>
    listClusterMentions(clusterId, mentionStatus),
  );
  const mentionRows = mentions.data?.mentions || [];

  /* -------------------------------------------------------------- gaps ---- */
  const gaps = useLoadable(clusterId, open.gaps, () =>
    listGaps({ cluster_id: clusterId, include_closed: true }),
  );
  const gapRows = gaps.data?.gaps || [];
  const gapFlagOn = Boolean(gaps.data?.flags?.ORION1_GAP_RESEARCH);
  const [gapBusy, setGapBusy] = useState("");
  const [gapResults, setGapResults] = useState<
    Record<string, { queries: number; allowanceUsd: number; items: GapSearchItem[] }>
  >({});
  const [gapErrors, setGapErrors] = useState<Record<string, string>>({});

  const gapFailure = (e: unknown): string => {
    if (e instanceof ApiError) {
      const detail = e.body?.detail;
      if (detail && typeof detail === "object" && !Array.isArray(detail)) {
        const record = detail as Record<string, unknown>;
        const reason = String(record.reason || record.stop_reason || "");
        const message = String(record.error || e.message);
        return reason ? `${message} [${reason}]` : message;
      }
    }
    return formatApiError(e);
  };

  const runGapResearch = async (gap: ResearchGap) => {
    if (gapBusy) return;
    setGapBusy(gap.gap_id);
    setGapErrors((prev) => ({ ...prev, [gap.gap_id]: "" }));
    try {
      const result = await researchGap(gap.gap_id);
      setGapResults((prev) => ({
        ...prev,
        [gap.gap_id]: {
          queries: result.queries,
          allowanceUsd: Number(result.gap.effective_allowance_usd ?? result.run_allowance_usd ?? 0),
          items: result.search_items || [],
        },
      }));
      toast(`${result.queries} follow-up quer${result.queries === 1 ? "y" : "ies"} planned`);
      gaps.reload();
    } catch (e) {
      const message = gapFailure(e);
      setGapErrors((prev) => ({ ...prev, [gap.gap_id]: message }));
      toast(message, true);
    } finally {
      setGapBusy("");
    }
  };

  const closeGapRow = async (gap: ResearchGap, status: string) => {
    if (gapBusy) return;
    setGapBusy(gap.gap_id);
    try {
      await closeGap(gap.gap_id, { status, stop_reason: "operator" });
      toast("Gap closed");
      gaps.reload();
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setGapBusy("");
    }
  };

  const reopenGapRow = async (gap: ResearchGap) => {
    if (gapBusy) return;
    setGapBusy(gap.gap_id);
    try {
      await reopenGap(gap.gap_id, { allowance_usd: gap.allowance_usd || 0 });
      toast("Gap reopened with a fresh attempt budget");
      gaps.reload();
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setGapBusy("");
    }
  };

  /* --------------------------------------------------------- decisions ---- */
  const decisions = useLoadable(clusterId, open.decisions, () =>
    listReconciliationDecisions({ cluster_id: clusterId, limit: 100 }),
  );
  const decisionRows = decisions.data?.decisions || [];

  /* ------------------------------------------------------------- spend ---- */
  const spend = useLoadable(clusterId, open.spend, () => clusterSpend(clusterId));

  /* ------------------------------------------------------------ chunks ---- */
  const chunks = useLoadable(clusterId, open.chunks, () =>
    listClusterChunkIssues({ cluster_id: clusterId, limit: 200 }),
  );
  const chunkRows = chunks.data?.chunks || [];

  if (!clusterId) {
    return (
      <SectionEmpty text="This bucket has no cluster id, so the v3 review queues cannot be scoped to it." />
    );
  }

  return (
    <div className="-mx-6 border-t border-outline-variant">
      <CollapsibleSection
        title="Routing review queue"
        meta={
          unresolved.loading && !holdRows.length
            ? "Loading…"
            : `${holdRows.length} held source${holdRows.length === 1 ? "" : "s"}${
                clearedCount ? ` · ${clearedCount} cleared here` : ""
              }`
        }
        collapsed={!open.unresolved}
        onToggle={() => toggle("unresolved")}
      >
        <div className="px-6 pb-4">
          {unresolved.error ? (
            <SectionError
              message={unresolved.error}
              onRetry={unresolved.reload}
              note={
                seedUnresolved.length
                  ? "Showing the queue captured with the last cluster load; it may be stale."
                  : undefined
              }
            />
          ) : null}
          <p className="text-[11px] text-on-surface-variant mb-3 leading-relaxed">
            Evidence the router held instead of routing. A hold is not a project: it is a source
            whose facts are not attached to anything until somebody decides.
          </p>
          {unresolved.loading && !holdRows.length ? (
            <SectionLoading label="Loading held sources…" />
          ) : holdRows.length ? (
            <ul className="space-y-2">
              {holdRows.map((row) => (
                <UnresolvedRow
                  key={row.unresolved_id}
                  row={row}
                  busy={holdBusy === row.unresolved_id}
                  onResolve={(target, status) => void resolveHold(target, status)}
                  onDecide={(target, verdict) => void decideHold(target, verdict)}
                />
              ))}
            </ul>
          ) : (
            <SectionEmpty
              text={
                clearedCount
                  ? "Queue clear — every held source has been resolved or dismissed."
                  : "No routing holds for this cluster. Every source the router saw was routed, or its hold was already cleared."
              }
            />
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Mentions"
        meta={sectionMeta(mentions, () => `${mentionRows.length} mention(s)`)}
        collapsed={!open.mentions}
        onToggle={() => toggle("mentions")}
      >
        <div className="px-6 pb-4 space-y-3">
          <div className="flex items-center gap-2">
            <FieldLabel className="shrink-0">Status</FieldLabel>
            <Select
              value={mentionStatus}
              onChange={(e) => setMentionStatus(e.target.value)}
              className="w-auto min-w-[200px]"
            >
              {MENTION_STATUS_OPTIONS.map((option) => (
                <option key={option.id || "all"} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Button size="sm" variant="ghost" onClick={mentions.reload} title="Reload mentions">
              <Icon name="refresh" className="text-base" />
            </Button>
          </div>
          {mentions.error ? (
            <SectionError message={mentions.error} onRetry={mentions.reload} />
          ) : mentions.loading ? (
            <SectionLoading label="Loading mentions…" />
          ) : mentionRows.length ? (
            <ul className="space-y-2">
              {mentionRows.map((mention) => (
                <MentionRow key={mention.mention_id} mention={mention} />
              ))}
            </ul>
          ) : (
            <SectionEmpty
              text={
                mentionStatus
                  ? `No mentions with status “${mentionStatus}” in this cluster.`
                  : "No mentions extracted for this cluster. Unified mention extraction runs only with ORION1_MENTIONS_V3 on; the legacy path writes no mentions."
              }
            />
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Research gaps"
        meta={sectionMeta(gaps, () => `${gaps.data?.open ?? 0} open · ${gapRows.length} total`)}
        collapsed={!open.gaps}
        onToggle={() => toggle("gaps")}
      >
        <div className="px-6 pb-4 space-y-3">
          <p className="text-[11px] text-on-surface-variant leading-relaxed">
            Follow-up research is bounded: every attempt is counted, a cooldown blocks the next one,
            and the planner refuses a gap once its attempts, cooldown or allowance say stop. Planning
            executes nothing — the queries go to the next search run.
            {gaps.loaded && !gaps.error && !gapFlagOn ? (
              <span className="text-tertiary">
                {" "}
                ORION1_GAP_RESEARCH is off, so the research action is refused with a 409.
              </span>
            ) : null}
          </p>
          {gaps.error ? (
            <SectionError message={gaps.error} onRetry={gaps.reload} />
          ) : gaps.loading ? (
            <SectionLoading label="Loading gaps…" />
          ) : gapRows.length ? (
            <ul className="space-y-2">
              {gapRows.map((gap) => (
                <GapRow
                  key={gap.gap_id}
                  gap={gap}
                  flagOn={gapFlagOn}
                  busy={gapBusy === gap.gap_id}
                  result={gapResults[gap.gap_id]}
                  error={gapErrors[gap.gap_id] || ""}
                  onResearch={(target) => void runGapResearch(target)}
                  onClose={(target, status) => void closeGapRow(target, status)}
                  onReopen={(target) => void reopenGapRow(target)}
                />
              ))}
            </ul>
          ) : (
            <SectionEmpty text="No recorded gaps for this cluster. Detection runs with ORION1_GAP_RESEARCH on; nothing is inferred here." />
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Reconciliation decision log"
        meta={sectionMeta(decisions, () => `${decisionRows.length} decision(s)`)}
        collapsed={!open.decisions}
        onToggle={() => toggle("decisions")}
      >
        <div className="px-6 pb-4 space-y-3">
          {decisions.error ? (
            <SectionError message={decisions.error} onRetry={decisions.reload} />
          ) : decisions.loading ? (
            <SectionLoading label="Loading decisions…" />
          ) : decisionRows.length ? (
            <ul className="space-y-2">
              {decisionRows.map((decision) => (
                <DecisionRow key={decision.decision_id} decision={decision} />
              ))}
            </ul>
          ) : (
            <SectionEmpty text="No persisted identity decisions for this cluster. Reconciliation writes these only with ORION1_RECONCILE_V3 on." />
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Spend"
        meta={sectionMeta(spend, () =>
          spend.data
            ? `${fmtUsd(spend.data.spend.cost_usd)} · ${
                spend.data.attributed ? "cluster-attributed" : "target-level"
              }`
            : "—",
        )}
        collapsed={!open.spend}
        onToggle={() => toggle("spend")}
      >
        <div className="px-6 pb-4">
          {spend.error ? (
            <SectionError message={spend.error} onRetry={spend.reload} />
          ) : spend.loading ? (
            <SectionLoading label="Loading spend…" />
          ) : spend.data ? (
            <ClusterSpendCard summary={spend.data.spend} targetName={targetName} />
          ) : (
            <SectionEmpty text="No spend recorded for this cluster." />
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Deferred and failed chunks"
        meta={sectionMeta(chunks, () => {
          const counts = chunks.data?.counts || {};
          const parts = Object.entries(counts).map(([name, count]) => `${count} ${name}`);
          return parts.length ? parts.join(" · ") : "none";
        })}
        collapsed={!open.chunks}
        onToggle={() => toggle("chunks")}
      >
        <div className="px-6 pb-4 space-y-3">
          <p className="text-[11px] text-on-surface-variant leading-relaxed">
            Chunks of this cluster's evidence that were never extracted — pages already paid for
            whose text nobody read.
          </p>
          {chunks.error ? (
            <SectionError message={chunks.error} onRetry={chunks.reload} />
          ) : chunks.loading ? (
            <SectionLoading label="Loading chunks…" />
          ) : chunkRows.length ? (
            <ul className="space-y-2">
              {chunkRows.map((chunk) => (
                <ChunkRow key={chunk.chunk_id} chunk={chunk} />
              ))}
            </ul>
          ) : (
            <SectionEmpty text="No deferred, skipped or failed chunks. Chunked extraction runs with ORION1_MENTIONS_V3 on; the legacy path stores no chunks." />
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}

export function VendorClustersPage() {
  const { toast } = useToast();
  const { draft } = useDraftState();
  const [params, setParams] = useSearchParams();
  const bucketParam = params.get("bucket") || "";
  const targetParam = params.get("target") || "";
  const urlJobId = params.get("job_id") || params.get("job") || "";
  const deskTarget = draft.target_company.trim();
  const effectiveTarget =
    targetParam === "all" ? "" : (targetParam || deskTarget).trim();
  const initialTargetFilter =
    targetParam === "all" ? ALL_TARGETS : targetParam || effectiveTarget;

  const [state, setState] = useState<Phase3State | null>(null);
  const [bucketId, setBucketId] = useState(bucketParam);
  const [filter, setFilter] = useState("");
  const [targetFilter, setTargetFilter] = useState(initialTargetFilter);
  const [jobTarget, setJobTarget] = useState("");
  const [model, setModel] = useState("");
  // "" = the original split: batch on the cheap model, escalation on the synthesis model.
  const [reconModel, setReconModel] = useState("");
  const [force, setForce] = useState(false);
  const [batchCostAckReady, setBatchCostAckReady] = useState(false);
  const [singleCostAckReady, setSingleCostAckReady] = useState(false);
  const { spend } = useDeskSpend();
  const [enableReasoning, setEnableReasoning] = useState(
    () => loadDeskPreferences().phase3_enable_pdr_reasoning,
  );
  const [batchBusy, setBatchBusy] = useState(false);
  const [trackedRuns, setTrackedRuns] = useState<Record<string, TrackedRun>>({});
  const [highlightRunId, setHighlightRunId] = useState("");
  const [dockExpanded, setDockExpanded] = useState(false);
  const [stoppingRun, setStoppingRun] = useState(false);
  const [jobsRefreshToken, setJobsRefreshToken] = useState(0);
  const [jobsPanelOpen, setJobsPanelOpen] = useState(false);
  const [promptPanelOpen, setPromptPanelOpen] = useState(false);
  const [promptTemplates, setPromptTemplates] = useState<SynthesisPromptTemplate[]>([]);
  const [promptLoading, setPromptLoading] = useState(false);
  const [pdrOpen, setPdrOpen] = useState(false);
  const [pdrText, setPdrText] = useState("");
  const [pdrSaving, setPdrSaving] = useState(false);
  const [pdrSchemaProfile, setPdrSchemaProfile] = useState<PdrSchemaProfile>(
    () => loadDeskPreferences().pdr_schema_profile,
  );
  const [pageGate, setPageGate] = useState<"loading" | "ready" | "no_target" | "no_job">(
    "loading",
  );
  const [pendingTarget, setPendingTarget] = useState("");
  const [resolvedJobId, setResolvedJobId] = useState(
    () => urlJobId || getStoredJobId() || "",
  );
  const pollRef = useRef<number | null>(null);
  const trackedRunsRef = useRef(trackedRuns);
  const batchBusyRef = useRef(batchBusy);
  const singleSubmittingRef = useRef(false);
  const [singleSubmitting, setSingleSubmitting] = useState(false);
  trackedRunsRef.current = trackedRuns;
  batchBusyRef.current = batchBusy;

  const activeRunIds = useMemo(
    () =>
      Object.values(trackedRuns)
        .filter((r) => ["pending", "running"].includes(r.status))
        .map((r) => r.runId),
    [trackedRuns],
  );
  const hasActiveRuns = activeRunIds.length > 0;
  const highlightRun = highlightRunId ? trackedRuns[highlightRunId] : undefined;

  const stopPoll = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const load = useCallback(
    async (opts?: { bucket?: string; target?: string; jobId?: string }) => {
      const jid = (opts?.jobId ?? resolvedJobId).trim();
      if (!jid) {
        throw new Error("job_id query parameter is required");
      }
      const q = new URLSearchParams();
      const bid = opts?.bucket ?? bucketId;
      const filterForApi =
        opts?.target !== undefined
          ? opts.target === "all"
            ? ALL_TARGETS
            : opts.target
          : targetFilter;
      const apiTarget = targetQueryForApi(filterForApi);
      q.set("job_id", jid);
      if (bid) q.set("bucket", bid);
      if (apiTarget) q.set("target", apiTarget);
      const data = await apiGet<{ state: Phase3State }>(`/api/phase3/state?${q}`);
      setState(data.state);
      const resolvedJobTarget = String(
        data.state.workspace?.target_company ||
          data.state.pipeline?.target_company ||
          "",
      );
      if (resolvedJobTarget) setJobTarget(resolvedJobTarget);
      const effectiveTarget = String(data.state.target_filter || "").trim();
      if (
        filterForApi !== ALL_TARGETS &&
        !filterForApi &&
        effectiveTarget
      ) {
        setTargetFilter(effectiveTarget);
        if (!targetParam && effectiveTarget) {
          setParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("job_id", jid);
            next.set("target", effectiveTarget);
            if (bid) next.set("bucket", bid);
            return next;
          }, { replace: true });
        }
      }
      const defaultModel =
        data.state.default_synthesis_model ||
        String(data.state.workspace?.phase3_synthesis_model || "");
      const catalog = data.state.synthesis_models || [];
      setModel((current) => {
        if (defaultModel) return defaultModel;
        if (!current && catalog[0]?.id) return String(catalog[0].id);
        return current;
      });
      const ws = data.state.workspace as Record<string, unknown> | undefined;
      setReconModel(String(ws?.reconciliation_model || ""));
      if (typeof ws?.phase3_enable_pdr_reasoning === "boolean") {
        setEnableReasoning(ws.phase3_enable_pdr_reasoning);
      } else if (typeof ws?.phase3_show_pdr_reasoning === "boolean") {
        setEnableReasoning(ws.phase3_show_pdr_reasoning);
      }
      if (typeof ws?.pdr_schema_profile === "string") {
        setPdrSchemaProfile(normalizePdrProfile(ws.pdr_schema_profile));
      } else if (typeof data.state.pdr_schema_profile === "string") {
        setPdrSchemaProfile(normalizePdrProfile(data.state.pdr_schema_profile));
      }
      if (typeof data.state.pdr_content === "string" && data.state.pdr_content) {
        setPdrText(data.state.pdr_content);
      }
      const selected =
        data.state.selected || data.state.selected_bucket || null;
      if (selected?.bucket_id) {
        setBucketId(String(selected.bucket_id));
      }
    },
    [bucketId, targetFilter, resolvedJobId, targetParam],
  );

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      setPageGate("loading");

      if (bucketParam) setBucketId(bucketParam);
      if (targetParam === "all") {
        setTargetFilter(ALL_TARGETS);
      } else if (targetParam) {
        setTargetFilter(targetParam);
      } else if (effectiveTarget) {
        setTargetFilter(effectiveTarget);
      }

      let jid = urlJobId || "";

      if (!jid && effectiveTarget) {
        setPendingTarget(effectiveTarget);
        if (!targetParam && deskTarget) {
          setParams({ target: deskTarget }, { replace: true });
        }
        const resolved = await resolveJobForDeskTarget(effectiveTarget);
        if (cancelled) return;
        if (resolved) {
          jid = resolved.jobId;
        } else if (!urlJobId) {
          setPageGate("no_job");
          return;
        }
      }

      if (!jid) {
        const stored = getStoredJobId();
        if (stored) jid = stored;
      }

      if (!jid) {
        setPageGate(effectiveTarget ? "no_job" : "no_target");
        return;
      }

      setResolvedJobId(jid);
      if (!params.get("job_id") && !params.get("job")) {
        const next = new URLSearchParams(params);
        next.set("job_id", jid);
        if (bucketParam) next.set("bucket", bucketParam);
        if (targetParam === "all") next.set("target", "all");
        else if (targetParam) next.set("target", targetParam);
        else if (effectiveTarget) next.set("target", effectiveTarget);
        setParams(next, { replace: true });
      }

      try {
        await load({
          bucket: bucketParam,
          target: targetParam === "all" ? "all" : targetParam || effectiveTarget || undefined,
          jobId: jid,
        });
        if (!cancelled) setPageGate("ready");
      } catch (e) {
        if (!cancelled) {
          toast(formatApiError(e), true);
          setPageGate("ready");
        }
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [urlJobId, effectiveTarget, targetParam, deskTarget, bucketParam]);

  const loadPdrReference = useCallback(
    (profile: PdrSchemaProfile) => {
      apiGet<{ pdr: { schema_text?: string; content?: string } }>(
        `/api/phase3/pdr?profile=${encodeURIComponent(profile)}`,
      )
        .then((d) => {
          const text = d.pdr?.schema_text || d.pdr?.content || "";
          if (text) setPdrText(text);
        })
        .catch(() => {});
    },
    [],
  );

  useEffect(() => {
    loadPdrReference(pdrSchemaProfile);
  }, [pdrSchemaProfile, loadPdrReference]);

  const persistPdrProfile = (profile: PdrSchemaProfile) => {
    setPdrSchemaProfile(profile);
    savePdrSchemaProfile(profile);
    loadPdrReference(profile);
  };

  const savePdrReference = async () => {
    if (pdrSaving) return;
    setPdrSaving(true);
    try {
      await apiPut("/api/phase3/pdr", {
        content: pdrText,
        profile: pdrSchemaProfile,
        note: "ui-save",
      });
      toast(`PDR reference saved (${pdrSchemaProfile})`);
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setPdrSaving(false);
    }
  };

  const persistReconciliationModel = useCallback(async (modelId: string) => {
    try {
      await apiPut("/api/workspace", { reconciliation_model: modelId });
    } catch {
      /* best-effort persistence */
    }
  }, []);

  const persistSynthesisModel = useCallback(async (modelId: string) => {
    if (!modelId) return;
    try {
      await apiPut("/api/workspace", { phase3_synthesis_model: modelId });
    } catch {
      /* best-effort persistence */
    }
  }, []);

  const buckets = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (state?.buckets || []).filter((b) => {
      if (!q) return true;
      const hay = `${bucketTarget(b)} ${bucketContractor(b)} ${b.bucket_id || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [state?.buckets, filter]);

  const selected = (state?.selected || state?.selected_bucket) as
    | (Phase3Bucket & Record<string, unknown>)
    | undefined;

  const models =
    state?.synthesis_models ||
    state?.openrouter_models?.cheap ||
    state?.openrouter_models?.pro ||
    [];

  const pendingTotal = Number(state?.pending_source_total ?? 0);

  const pendingByTarget = useMemo(() => {
    const byTarget = new Map<string, number>();
    let summed = 0;
    for (const b of state?.buckets || []) {
      const t = bucketTarget(b);
      if (!t || t === "—") continue;
      const pending = Number(b.pending_source_count ?? b.pending_count ?? 0);
      byTarget.set(t, (byTarget.get(t) || 0) + pending);
      summed += pending;
    }
    return { byTarget, total: summed || pendingTotal };
  }, [state?.buckets, pendingTotal]);

  const formatTargetOptionLabel = (name: string, pending: number) => {
    if (pending <= 0) return name;
    return `${name} (${pending} pending source${pending === 1 ? "" : "s"})`;
  };

  const showingAllTargets = targetFilter === ALL_TARGETS;
  const activeTargetName = showingAllTargets
    ? ""
    : targetFilter || jobTarget;

  const processableBuckets = useMemo(() => {
    return (state?.buckets || []).filter((b) => {
      if (activeTargetName && bucketTarget(b) !== activeTargetName) return false;
      const pending = Number(b.pending_source_count ?? b.pending_count ?? 0);
      const total = Number(b.evidence_count ?? 0);
      return pending > 0 && total > 0;
    });
  }, [state?.buckets, activeTargetName]);

  const trackRun = useCallback(
    (runId: string, meta: Omit<TrackedRun, "runId" | "status" | "progress" | "log" | "error">) => {
      setTrackedRuns((prev) => ({
        ...prev,
        [runId]: {
          runId,
          status: "pending",
          progress: null,
          log: null,
          error: "",
          ...meta,
        },
      }));
      setHighlightRunId(runId);
      setJobsRefreshToken((t) => t + 1);
    },
    [],
  );

  const pollActiveRuns = useCallback(async () => {
    const active = Object.values(trackedRunsRef.current).filter((r) =>
      ["pending", "running"].includes(r.status),
    );
    if (!active.length) return;

    let anyStillActive = false;
    let synthesized = 0;
    let discovered = 0;
    const terminal: { status: string; error: string }[] = [];

    await Promise.all(
      active.map(async ({ runId }) => {
        try {
          const data = await apiGet<
            Phase1JobPoll & {
              result?: {
                projects_synthesized?: number;
                projects_discovered?: number;
                state?: Phase3State;
              };
            }
          >(`/api/jobs/${encodeURIComponent(runId)}?lite=true`);
          const status = String(data.status || "");
          setTrackedRuns((prev) => {
            const cur = prev[runId];
            if (!cur) return prev;
            return {
              ...prev,
              [runId]: {
                ...cur,
                status,
                progress: (data.progress as Phase3Progress) || null,
                log: data.log || null,
                error: status === "error" ? data.error || "PDR synthesis failed" : "",
              },
            };
          });
          if (status === "running" || status === "pending") {
            anyStillActive = true;
            return;
          }
          terminal.push({
            status,
            error: status === "error" ? data.error || "PDR synthesis failed" : "",
          });
          const result = data.result || {};
          synthesized += Number(result.projects_synthesized ?? 0);
          discovered += Number(result.projects_discovered ?? 0);
          if (status === "ok" && result.state) {
            setState(result.state);
          }
        } catch {
          anyStillActive = true;
        }
      }),
    );

    if (!anyStillActive && active.length) {
      stopPoll();
      if (batchBusyRef.current) {
        return;
      }
      setBatchBusy(false);
      await load({ bucket: bucketId, target: targetFilter }).catch(() => {});
      setJobsRefreshToken((t) => t + 1);
      if (active.length === 1) {
        const t0 = terminal[0];
        if (t0?.status === "cancelled") {
          toast("PDR synthesis stopped");
        } else if (t0?.status === "error") {
          toast(t0.error || "PDR synthesis failed", true);
        } else {
          toast(`PDR complete — ${synthesized} updated, ${discovered} discovered`);
        }
      } else {
        const errors = terminal.filter((t) => t.status === "error").length;
        toast(
          errors
            ? `Batch finished — ${active.length - errors} ok, ${errors} failed`
            : `Batch complete — ${active.length} bucket(s) processed`,
          Boolean(errors),
        );
      }
    }
  }, [bucketId, targetFilter, load, toast]);

  useEffect(() => {
    if (!hasActiveRuns) {
      stopPoll();
      return;
    }
    void pollActiveRuns();
    pollRef.current = window.setInterval(() => void pollActiveRuns(), 2500);
    return () => stopPoll();
  }, [hasActiveRuns, pollActiveRuns]);

  useEffect(() => () => stopPoll(), []);

  const persistEnableReasoning = (on: boolean) => {
    setEnableReasoning(on);
    savePhase3EnablePdrReasoning(on);
  };

  const waitForRunTerminal = async (runId: string): Promise<string> => {
    const deadline = Date.now() + 45 * 60 * 1000;
    while (Date.now() < deadline) {
      const data = await apiGet<Phase1JobPoll>(`/api/jobs/${encodeURIComponent(runId)}?lite=true`);
      const status = String(data.status || "");
      setTrackedRuns((prev) => {
        const cur = prev[runId];
        if (!cur) return prev;
        return {
          ...prev,
          [runId]: {
            ...cur,
            status,
            progress: (data.progress as Phase3Progress) || null,
            log: data.log || null,
            error: status === "error" ? data.error || "PDR synthesis failed" : "",
          },
        };
      });
      if (!["pending", "running"].includes(status)) {
        return status;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 2000));
    }
    return "error";
  };

  const startSynthesisForBucket = async (
    b: Phase3Bucket,
    opts?: { silent?: boolean; includeReasoning?: boolean },
  ) => {
    const bid = b.bucket_id;
    if (!bid || !model) {
      toast("Select a synthesis model first", true);
      return null;
    }
    const includeReasoning = opts?.includeReasoning ?? enableReasoning;
    const started = await apiPost<{ run_id: string }>(
      `/api/phase3/buckets/${encodeURIComponent(bid)}/synthesize`,
      {
        model,
        force,
        include_reasoning: includeReasoning,
        job_id: resolvedJobId,
      },
    );
    if (!started.run_id) throw new Error("No run_id returned");
    trackRun(started.run_id, {
      bucketId: bid,
      targetName: bucketTarget(b),
      contractorName: bucketContractor(b),
    });
    if (!opts?.silent) {
      toast(`PDR synthesis queued — ${bucketContractor(b)}`);
    }
    return started.run_id;
  };

  const synthesize = async () => {
    if (
      singleSubmittingRef.current ||
      batchBusyRef.current ||
      batchBusy ||
      hasActiveRuns
    ) {
      return;
    }
    if (!selected?.bucket_id || !model) {
      toast("Select a vendor cluster and synthesis model", true);
      return;
    }
    singleSubmittingRef.current = true;
    setSingleSubmitting(true);
    try {
      await startSynthesisForBucket(selected as Phase3Bucket);
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      singleSubmittingRef.current = false;
      setSingleSubmitting(false);
    }
  };

  const processAllPending = async () => {
    if (batchBusyRef.current || batchBusy || hasActiveRuns) return;
    if (!model) {
      toast("Select a synthesis model first", true);
      return;
    }
    if (!processableBuckets.length) {
      toast(force ? "No buckets to process" : "No buckets with pending sources", true);
      return;
    }
    batchBusyRef.current = true;
    setBatchBusy(true);
    const reasoningForBatch = enableReasoning;
    let processed = 0;
    let failed = 0;
    try {
      for (const b of processableBuckets) {
        try {
          const runId = await startSynthesisForBucket(b, {
            silent: true,
            includeReasoning: reasoningForBatch,
          });
          if (!runId) continue;
          const status = await waitForRunTerminal(runId);
          if (status === "ok") {
            processed += 1;
            await load({ bucket: bucketId, target: targetFilter }).catch(() => {});
          } else if (status !== "cancelled") {
            failed += 1;
            toast(`${bucketContractor(b)}: PDR synthesis ${status}`, true);
          }
        } catch (e) {
          failed += 1;
          toast(`${bucketContractor(b)}: ${formatApiError(e)}`, true);
        }
      }
      if (processed || failed) {
        toast(
          failed
            ? `Process all — ${processed} ok, ${failed} failed`
            : `Process all complete — ${processed} bucket${processed === 1 ? "" : "s"}`,
          Boolean(failed),
        );
        setJobsRefreshToken((t) => t + 1);
      }
    } finally {
      batchBusyRef.current = false;
      setBatchBusy(false);
      await load({ bucket: bucketId, target: targetFilter }).catch(() => {});
    }
  };

  const stopHighlightedRun = async () => {
    if (!highlightRunId || stoppingRun) return;
    setStoppingRun(true);
    try {
      await apiPost(`/api/jobs/${encodeURIComponent(highlightRunId)}/cancel`, {});
      setTrackedRuns((prev) => {
        const cur = prev[highlightRunId];
        if (!cur) return prev;
        return { ...prev, [highlightRunId]: { ...cur, status: "cancelled" } };
      });
      setJobsRefreshToken((t) => t + 1);
      toast("PDR synthesis stopped");
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setStoppingRun(false);
    }
  };

  const selectBucket = (bid: string) => {
    setBucketId(bid);
    const next = new URLSearchParams(params);
    next.set("job_id", resolvedJobId);
    next.set("bucket", bid);
    if (targetFilter === ALL_TARGETS) {
      next.set("target", "all");
    } else {
      const t = targetFilter || jobTarget;
      if (t) next.set("target", t);
    }
    setParams(next, { replace: true });
    load({ bucket: bid }).catch((e) => toast(formatApiError(e), true));
  };

  const targets = useMemo(() => {
    const names = new Set<string>();
    for (const t of state?.targets || []) {
      if (t) names.add(t);
    }
    for (const b of state?.buckets || []) {
      const t = bucketTarget(b);
      if (t && t !== "—") names.add(t);
    }
    if (jobTarget) names.add(jobTarget);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [state?.targets, state?.buckets, jobTarget]);

  /* ---------------------------------------- leads for whole vendor clusters -- */

  const [selectedBuckets, setSelectedBuckets] = useState<Set<string>>(() => new Set());
  const [leadSummaries, setLeadSummaries] = useState<Record<string, LeadProjectSummary>>({});
  const [clusterLeadFilter, setClusterLeadFilter] = useState<ClusterLeadFilter>("all");
  const [clusterOriginFilter, setClusterOriginFilter] = useState<ClusterOriginFilter>("all");

  // With a lead filter set, a cluster is acted on for its matching projects
  // only: "clusters with projects without leads" generates for exactly those.
  const projectFilter: LeadFilter | "" =
    clusterLeadFilter === "all" || clusterLeadFilter === "done" ? "" : clusterLeadFilter;
  // A project found via another project's lead research never gets leads of its
  // own (no second round), whatever the filter.
  const projectsToActOn = useCallback(
    (b: Phase3Bucket) =>
      bucketProjectIds(b).filter(
        (pid) =>
          !isFoundViaLead(leadSummaries[pid]) &&
          (!projectFilter || matchesLeadFilter(leadSummaries[pid], projectFilter)),
      ),
    [projectFilter, leadSummaries],
  );
  const clusterMatches = useCallback(
    (b: Phase3Bucket, filter: ClusterLeadFilter) => {
      const ids = bucketProjectIds(b);
      if (filter === "all") return true;
      if (filter === "done") {
        const own = ids.filter((pid) => !isFoundViaLead(leadSummaries[pid]));
        return own.length > 0 && own.every((pid) => matchesLeadFilter(leadSummaries[pid], "has"));
      }
      return ids.some((pid) => matchesLeadFilter(leadSummaries[pid], filter));
    },
    [leadSummaries],
  );
  const leadFilteredBuckets = useMemo(
    () =>
      buckets.filter(
        (b) => clusterMatches(b, clusterLeadFilter) && originMatches(b, clusterOriginFilter),
      ),
    [buckets, clusterMatches, clusterLeadFilter, clusterOriginFilter],
  );
  const originFilterCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const option of CLUSTER_ORIGIN_FILTERS) {
      counts[option.id] = buckets.filter((b) => originMatches(b, option.id)).length;
    }
    return counts;
  }, [buckets]);
  const clusterFilterCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const option of CLUSTER_LEAD_FILTERS) {
      counts[option.id] = buckets.filter((b) => leadEligible(b) && clusterMatches(b, option.id)).length;
    }
    return counts;
  }, [buckets, clusterMatches]);

  const eligibleVisible = useMemo(
    () => leadFilteredBuckets.filter((b) => leadEligible(b) && projectsToActOn(b).length > 0),
    [leadFilteredBuckets, projectsToActOn],
  );
  const allEligibleVisibleSelected =
    eligibleVisible.length > 0 && eligibleVisible.every((b) => selectedBuckets.has(b.bucket_id));
  // Only ticked clusters that the current filter shows are acted on: a cluster
  // ticked earlier and then filtered out must not ride along unseen.
  const selectedLeadBuckets = useMemo(
    () =>
      leadFilteredBuckets.filter(
        (b) => selectedBuckets.has(b.bucket_id) && leadEligible(b) && projectsToActOn(b).length > 0,
      ),
    [leadFilteredBuckets, selectedBuckets, projectsToActOn],
  );
  const selectedLeadProjectIds = useMemo(
    () => [...new Set(selectedLeadBuckets.flatMap(projectsToActOn))],
    [selectedLeadBuckets, projectsToActOn],
  );
  const allBucketProjectIds = useMemo(
    () => [...new Set((state?.buckets || []).flatMap(bucketProjectIds))],
    [state?.buckets],
  );
  const allBucketProjectKey = allBucketProjectIds.join(",");

  const loadLeadSummaries = useCallback(async (ids: string[]) => {
    if (!ids.length) {
      setLeadSummaries({});
      return;
    }
    try {
      const merged: Record<string, LeadProjectSummary> = {};
      // The summaries route takes at most 500 ids per request.
      for (let i = 0; i < ids.length; i += 400) {
        const data = await leadProjectSummaries(ids.slice(i, i + 400));
        Object.assign(merged, data.summaries || {});
      }
      setLeadSummaries(merged);
    } catch {
      /* lead badges are a convenience; the list stays usable without them */
    }
  }, []);

  useEffect(() => {
    void loadLeadSummaries(allBucketProjectKey ? allBucketProjectKey.split(",") : []);
  }, [allBucketProjectKey, loadLeadSummaries]);

  const toggleBucketForLeads = (bid: string, on: boolean) => {
    setSelectedBuckets((prev) => {
      const next = new Set(prev);
      if (on) next.add(bid);
      else next.delete(bid);
      return next;
    });
  };

  const toggleAllEligibleVisible = (on: boolean) => {
    setSelectedBuckets((prev) => {
      const next = new Set(prev);
      for (const b of eligibleVisible) {
        if (on) next.add(b.bucket_id);
        else next.delete(b.bucket_id);
      }
      return next;
    });
  };

  const bucketLeadCounts = (b: Phase3Bucket) => {
    let open = 0;
    let missing = 0;
    let outdated = 0;
    let found = 0;
    let viaLead = 0;
    for (const pid of bucketProjectIds(b)) {
      const summary = leadSummaries[pid];
      if (!summary) continue;
      open += summary.open_leads || 0;
      found += summary.found_projects || 0;
      if (isFoundViaLead(summary)) viaLead += 1;
      else if (!hasGeneratedLeads(summary)) missing += 1;
      else if (summary.stale) outdated += 1;
    }
    return { open, missing, outdated, found, viaLead };
  };

  const displayedTarget = scopeLabel(targetFilter, jobTarget);

  const selectedPending = Number(selected?.pending_source_count ?? 0);
  const selectedTotal = Number(selected?.evidence_count ?? 0);
  // The v3 review sections are scoped to the cluster, not the legacy bucket id.
  const selectedClusterId = String(selected?.cluster_id || state?.cluster_id || "");
  // build_phase3_state already returns this cluster's M0 routing holds, so the
  // queue renders on the first paint and the section's own fetch only refreshes
  // it; a failed refresh falls back to these rows instead of showing nothing.
  const seedUnresolved = useMemo<UnresolvedRouting[]>(
    () => (Array.isArray(selected?.unresolved) ? (selected?.unresolved as UnresolvedRouting[]) : []),
    [selected?.unresolved],
  );
  const singleBusy =
    singleSubmitting ||
    (hasActiveRuns &&
      Boolean(
        highlightRun?.bucketId &&
        highlightRun.bucketId === bucketId &&
        ["pending", "running"].includes(highlightRun.status),
      ));
  const canProcess = selectedPending > 0 && selectedTotal > 0 && !singleBusy && !batchBusy;
  const panelTarget =
    activeTargetName ||
    highlightRun?.targetName ||
    bucketTarget(selected || ({} as Phase3Bucket));
  const jobsRunning = hasActiveRuns || batchBusy || singleBusy;

  const batchControls = (
    <>
      <CostAcknowledgementGate
        phase="phase3_synthesize"
        jobId={resolvedJobId || undefined}
        model={model}
        force={force}
        bucketCount={processableBuckets.length || 1}
        estimateKey={`batch-${model}-${force}-${processableBuckets.length}-${enableReasoning}`}
        onReadyChange={setBatchCostAckReady}
      />
      <Button
        variant="accent"
        size="sm"
        onClick={() => void processAllPending()}
        disabled={
          batchBusy ||
          hasActiveRuns ||
          !model ||
          spend.desk_locked ||
          !batchCostAckReady ||
          processableBuckets.length === 0
        }
        title={
          processableBuckets.length === 0
            ? force
              ? "No buckets to process"
              : "No buckets with pending sources"
            : force
              ? `Queue PDR synthesis for all buckets (${enableReasoning ? "with" : "without"} audit reasoning)`
              : `Queue PDR synthesis for every bucket with pending sources (${enableReasoning ? "with" : "without"} audit reasoning)`
        }
      >
        {batchBusy || hasActiveRuns
          ? "Processing…"
          : `Process all (${processableBuckets.length})`}
      </Button>
    </>
  );

  const singleBucketControls = selected ? (
    <>
      <CostAcknowledgementGate
        phase="phase3_synthesize"
        jobId={resolvedJobId || undefined}
        model={model}
        force={force}
        bucketCount={1}
        estimateKey={`single-${model}-${force}-${selected?.bucket_id || ""}-${enableReasoning}`}
        onReadyChange={setSingleCostAckReady}
      />
      <Button
        variant="accent"
        size="sm"
        onClick={() => void synthesize()}
        disabled={
          singleBusy ||
          batchBusy ||
          singleSubmitting ||
          spend.desk_locked ||
          !singleCostAckReady ||
          (!canProcess && !force)
        }
        title={
          force
            ? "Re-run PDR on all sources in this cluster"
            : canProcess
              ? "Run PDR discovery + synthesis on pending sources"
              : "No pending sources — enable Force to re-process"
        }
      >
        {(singleBusy || batchBusy || singleSubmitting) && <span className="orion-spinner" />}
        {singleBusy || batchBusy || singleSubmitting
          ? "Processing…"
          : force
            ? "Force process bucket"
            : canProcess
              ? selectedPending === 1
                ? "Process 1 new source"
                : `Process ${selectedPending} new sources`
              : "Up to date"}
      </Button>
    </>
  ) : null;

  const synthesisControls = (
    <>
      <label
        className="flex items-center gap-1.5 text-body-sm shrink-0 whitespace-nowrap"
        title="Model that writes the PDR for each project."
      >
        Synthesis
        <Select
          value={model}
          onChange={(e) => {
            const next = e.target.value;
            setModel(next);
            void persistSynthesisModel(next);
          }}
          className="min-w-[200px] max-w-[280px] w-auto shrink-0"
        >
          {models.length ? (
            models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))
          ) : (
            <option value="">Sync models in Phase 1</option>
          )}
        </Select>
      </label>
      {state?.reconcile_v3 ? (
        <label
          className="flex items-center gap-1.5 text-body-sm shrink-0 whitespace-nowrap"
          title="Model that matches new mentions to existing projects and vendors before synthesis. A wrong merge here is hard to undo. Default keeps batch calls on the cheap model and escalations on the synthesis model."
        >
          Reconciliation
          <Select
            value={reconModel}
            onChange={(e) => {
              const next = e.target.value;
              setReconModel(next);
              void persistReconciliationModel(next);
            }}
            className="min-w-[200px] max-w-[280px] w-auto shrink-0"
          >
            <option value="">Default (cheap batch, synthesis escalation)</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Select>
        </label>
      ) : null}
      <label className="flex items-center gap-1 text-body-sm shrink-0 whitespace-nowrap">
        <input
          type="checkbox"
          checked={force}
          disabled={spend.desk_locked}
          onChange={(e) => setForce(e.target.checked)}
        />
        Force re-process
      </label>
      <label
        className="flex items-center gap-1.5 text-body-sm shrink-0 whitespace-nowrap"
        title="When enabled, the model writes audit reasoning for PDR fields (more tokens). Off is faster and cheaper."
      >
        <input
          type="checkbox"
          checked={enableReasoning}
          onChange={(e) => persistEnableReasoning(e.target.checked)}
          disabled={batchBusy || hasActiveRuns}
        />
        Enable reasoning
      </label>
    </>
  );

  if (pageGate === "loading") {
    return (
      <main className="flex-1 flex flex-col items-center justify-center gap-3 min-h-0">
        <span className="orion-spinner w-8 h-8 border-[3px]" aria-hidden />
        <p className="text-body-sm text-on-surface-variant">Loading vendor clusters…</p>
      </main>
    );
  }

  if (pageGate === "no_target") {
    return (
      <DeskTargetGate
        phaseNum={3}
        phaseLabel="Vendor Clusters"
        title="PDR synthesis by contractor"
        mode="no_target"
      />
    );
  }

  if (pageGate === "no_job") {
    return (
      <DeskTargetGate
        phaseNum={3}
        phaseLabel="Vendor Clusters"
        title="PDR synthesis by contractor"
        mode="no_job"
        targetName={pendingTarget || effectiveTarget}
      />
    );
  }

  return (
    <main className="flex-1 flex flex-col h-full overflow-hidden min-w-0 bg-background">
      <div className="shrink-0 border-b border-outline-variant bg-surface-container">
        <PageTitle
          phaseNum={3}
          phaseLabel="Vendor clusters"
          title="PDR synthesis by contractor"
          meta={
            jobsRunning
              ? "PDR synthesis in progress — open Jobs for run history"
              : "Each cluster is a target + main contractor from Phase 2 — Jobs drawer for synthesis runs"
          }
          actions={
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPromptPanelOpen(true);
                  setPromptLoading(true);
                  fetchSynthesisPrompts()
                    .then(setPromptTemplates)
                    .catch((err) => toast(formatApiError(err), true))
                    .finally(() => setPromptLoading(false));
                }}
                aria-controls="phase3-prompt-panel"
                title="Master prompt and synthesis instructions sent on every PDR synthesis call"
              >
                <Icon name="edit_note" className="text-base" />
                Synthesis prompt
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setJobsPanelOpen((open) => !open)}
                aria-expanded={jobsPanelOpen}
                aria-controls="phase3-jobs-panel"
                className={jobsPanelOpen ? "text-primary bg-primary/10" : undefined}
              >
                {jobsRunning ? <span className="orion-spinner" aria-hidden /> : null}
                <Icon name="schedule" className="text-base" />
                Jobs
              </Button>
            </>
          }
        />

        <Drawer
          id="phase3-prompt-panel"
          icon="edit_note"
          open={promptPanelOpen}
          onClose={() => setPromptPanelOpen(false)}
          title="Synthesis prompt"
          subtitle="Master prompt + synthesis instructions, sent on every PDR synthesis call"
        >
          <SynthesisPromptEditor
            mode="production"
            templates={promptTemplates}
            loading={promptLoading}
            onTemplatesChange={setPromptTemplates}
            targetCompany={panelTarget && panelTarget !== "—" ? panelTarget : ""}
            pdrProfile={pdrSchemaProfile}
            className="flex-1"
          />
        </Drawer>

        <Drawer
          id="phase3-jobs-panel"
          open={jobsPanelOpen}
          onClose={() => setJobsPanelOpen(false)}
          title="PDR synthesis jobs"
          subtitle={
            panelTarget && panelTarget !== "—"
              ? panelTarget
              : "Filter by target or select a cluster to scope runs"
          }
        >
          <TargetJobRunsPanel
            mode="synthesis"
            targetName={panelTarget === "—" ? "" : panelTarget}
            embedded
            fillHeight
            showToggle={false}
            defaultCollapsed={false}
            refreshToken={jobsRefreshToken}
            highlightRunId={highlightRunId}
            liveProgress={highlightRun?.progress ?? null}
            onOpenRun={(runId) => {
              setJobsPanelOpen(false);
              setHighlightRunId(runId);
            }}
            onActiveRun={(runId) => {
              setHighlightRunId(runId);
              setTrackedRuns((prev) => {
                if (prev[runId]) return prev;
                return {
                  ...prev,
                  [runId]: {
                    runId,
                    bucketId: "",
                    targetName: panelTarget === "—" ? "" : panelTarget,
                    contractorName: "",
                    status: "running",
                    progress: null,
                    log: null,
                    error: "",
                  },
                };
              });
            }}
          />
        </Drawer>

        <div className="border-t border-outline-variant/50 px-6 py-2 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-[10px] uppercase text-on-surface-variant">
            PDR schema
            <Select
              value={pdrSchemaProfile}
              onChange={(e) => persistPdrProfile(normalizePdrProfile(e.target.value))}
              className="min-w-[200px] normal-case text-body-sm w-auto"
            >
              {PDR_PROFILE_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </Select>
          </label>
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => void savePdrReference()}
              disabled={pdrSaving || !pdrText.trim()}
              title="Save PDR reference to workspace"
            >
              {pdrSaving ? "Saving…" : "Save PDR"}
            </Button>
            <button
              type="button"
              onClick={() => setPdrOpen((o) => !o)}
              className="flex items-center gap-2 text-[10px] uppercase text-on-surface-variant"
            >
              <Icon name="description" /> Reference
              <Icon name={pdrOpen ? "expand_less" : "expand_more"} />
            </button>
          </div>
        </div>
        {pdrOpen && (
          <textarea
            value={pdrText}
            onChange={(e) => setPdrText(e.target.value)}
            className="min-h-[33vh] max-h-[33vh] w-full overflow-y-auto px-6 pb-3 pt-2 font-data-mono text-[11px] text-on-surface-variant whitespace-pre-wrap border-t border-outline-variant/40 custom-scrollbar bg-transparent resize-none focus:outline-none"
            spellCheck={false}
          />
        )}
        <div className="border-t border-outline-variant/50 pt-2">
          <p className="px-6 pb-1 text-[10px] uppercase tracking-widest text-tertiary font-data-label">
            Possible next projects · filter the list, tick clusters with projects, generate, then see them in
            Search → Lead research
          </p>
          <LeadBulkToolbar
            projectIds={selectedLeadProjectIds}
            selectionCount={selectedLeadBuckets.length}
            unit={CLUSTER_UNIT}
            visibleCount={eligibleVisible.length}
            allVisibleSelected={allEligibleVisibleSelected}
            onToggleAll={toggleAllEligibleVisible}
            selectAllLabel={`Select all eligible shown (${eligibleVisible.length} of ${leadFilteredBuckets.length})`}
            sendToSearchDefault
            storageKey="orion1.leads.clusterRun"
            onChanged={() => void loadLeadSummaries(allBucketProjectIds)}
          />
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden min-h-0">
        <section className="w-[320px] lg:w-[380px] border-r border-outline-variant flex flex-col bg-surface-container-lowest shrink-0">
          <div className="p-4 border-b border-outline-variant space-y-3">
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter vendor clusters…"
            />
            <Select
              value={clusterLeadFilter}
              onChange={(e) => setClusterLeadFilter(e.target.value as ClusterLeadFilter)}
              aria-label="Filter vendor clusters by lead coverage"
              className="py-1"
              title="With a lead filter, Generate leads acts only on the matching projects of the ticked clusters"
            >
              {CLUSTER_LEAD_FILTERS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label} ({clusterFilterCounts[option.id] ?? 0})
                </option>
              ))}
            </Select>
            <Select
              value={clusterOriginFilter}
              onChange={(e) => setClusterOriginFilter(e.target.value as ClusterOriginFilter)}
              aria-label="Filter vendor clusters by where their sources came from"
              className="py-1"
              title="Initial search, or the second iteration: sources that lead research brought in first"
            >
              {CLUSTER_ORIGIN_FILTERS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label} ({originFilterCounts[option.id] ?? 0})
                </option>
              ))}
            </Select>
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wide text-on-surface-variant font-bold">
                Target
              </p>
              <p
                className="text-body-sm font-semibold text-on-surface truncate"
                title={displayedTarget}
              >
                {displayedTarget}
              </p>
              <Select
                value={selectTargetValue(targetFilter, jobTarget)}
                onChange={(e) => {
                  const t = e.target.value;
                  setTargetFilter(t);
                  const next = new URLSearchParams();
                  next.set("job_id", resolvedJobId);
                  if (t === ALL_TARGETS) next.set("target", "all");
                  else if (t) next.set("target", t);
                  if (bucketId) next.set("bucket", bucketId);
                  setParams(next, { replace: true });
                  load({
                    target: t === ALL_TARGETS ? "all" : t,
                    bucket: bucketId,
                  }).catch((err) => toast(formatApiError(err), true));
                }}
              >
                <option value={ALL_TARGETS}>
                  {formatTargetOptionLabel("All targets", pendingByTarget.total)}
                </option>
                {targets.map((t) => (
                  <option key={t} value={t}>
                    {formatTargetOptionLabel(t, pendingByTarget.byTarget.get(t) || 0)}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {leadFilteredBuckets.map((b) => {
              const status = bucketStatusLabel(b);
              const isActive = bucketId === b.bucket_id;
              const hasProjects = leadEligible(b);
              const eligible = projectsToActOn(b).length > 0;
              const leads = bucketLeadCounts(b);
              return (
                <div
                  key={`${b.bucket_id}::${bucketContractor(b)}`}
                  className={`flex items-start gap-2 border-b border-outline-variant ${
                    isActive
                      ? "border-l-4 border-l-tertiary bg-surface-container-high"
                      : "hover:bg-surface-container border-l-4 border-l-transparent"
                  }`}
                >
                <input
                  type="checkbox"
                  className="mt-5 ml-3 shrink-0"
                  checked={eligible && selectedBuckets.has(b.bucket_id)}
                  disabled={!eligible}
                  onChange={(e) => toggleBucketForLeads(b.bucket_id, e.target.checked)}
                  title={
                    eligible
                      ? "Select this cluster's projects for lead generation"
                      : hasProjects
                        ? "Nothing to generate here: its projects were found via lead research or do not match the lead filter"
                        : "No projects in this cluster yet: process it first"
                  }
                  aria-label={`Select ${bucketContractor(b)} for lead generation`}
                />
                <button
                  type="button"
                  onClick={() => selectBucket(b.bucket_id)}
                  className="flex-1 min-w-0 text-left py-4 pr-4"
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className="text-body-sm font-semibold text-on-surface truncate">
                        {bucketTarget(b)}
                      </p>
                      <p className="text-[11px] font-data-mono text-primary uppercase tracking-tight mt-0.5 truncate">
                        {bucketContractor(b)}
                      </p>
                      {b.source_origin === "lead_research" ? (
                        <span
                          className="inline-block mt-1 text-[9px] font-bold px-2 py-0.5 rounded border uppercase bg-tertiary/15 text-tertiary border-tertiary/40"
                          title={`Second iteration: all ${b.lead_source_count ?? ""} source(s) were first found by lead research`}
                        >
                          2nd iteration
                        </span>
                      ) : b.source_origin === "mixed" ? (
                        <span
                          className="inline-block mt-1 text-[9px] font-bold px-2 py-0.5 rounded border uppercase border-tertiary/40 text-tertiary"
                          title={`${b.lead_source_count} of ${b.evidence_count ?? "?"} sources were first found by lead research`}
                        >
                          +{b.lead_source_count} from leads
                        </span>
                      ) : null}
                    </div>
                    {status ? (
                      <span
                        className={`shrink-0 text-[9px] font-bold px-2 py-0.5 rounded border uppercase ${
                          status.includes("new source")
                            ? "bg-tertiary/10 text-tertiary border-tertiary/20"
                            : status === "Up to date"
                              ? "bg-secondary/10 text-secondary border-secondary/20"
                              : "bg-surface-container-highest text-on-surface-variant border-outline-variant"
                        }`}
                      >
                        {status}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-[10px] text-on-surface-variant font-data-mono mt-2">
                    {b.evidence_synthesized_count ?? 0} / {b.evidence_count ?? 0}{" "}
                    sources synthesized
                    {(b.project_count ?? 0) > 0
                      ? ` · ${b.project_count} project${b.project_count === 1 ? "" : "s"}`
                      : ""}
                  </p>
                  {leads.open || leads.missing || leads.outdated || leads.found || leads.viaLead ? (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {leads.open ? (
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase bg-tertiary/10 text-tertiary border-tertiary/20">
                          {leads.open} lead{leads.open === 1 ? "" : "s"}
                        </span>
                      ) : null}
                      {leads.found ? (
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase bg-primary/10 text-primary border-primary/20">
                          {leads.found} found
                        </span>
                      ) : null}
                      {leads.missing ? (
                        <span
                          className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase border-secondary/40 text-secondary"
                          title="Projects in this cluster with no generated leads yet"
                        >
                          {leads.missing} without leads
                        </span>
                      ) : null}
                      {leads.viaLead ? (
                        <span
                          className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase bg-tertiary/10 text-tertiary border-tertiary/30"
                          title="Projects found via another project's lead research; leads are not generated for them"
                        >
                          {leads.viaLead} found via lead
                        </span>
                      ) : null}
                      {leads.outdated ? (
                        <span
                          className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase border-secondary/40 text-secondary"
                          title="Projects whose roadmap changed after their leads were generated"
                        >
                          {leads.outdated} out of date
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </button>
                </div>
              );
            })}
            {buckets.length > 0 && !leadFilteredBuckets.length ? (
              <p className="p-4 text-body-sm text-on-surface-variant leading-relaxed">
                No vendor clusters match this lead filter.
              </p>
            ) : null}
            {!buckets.length && (
              <p className="p-4 text-body-sm text-on-surface-variant leading-relaxed">
                No vendor clusters yet. Run Phase 1 search, then Phase 2 contractor
                extraction on the Evidence page. Each extracted main contractor
                appears here as a cluster for PDR synthesis.
              </p>
            )}
          </div>
        </section>

        <section className="flex-1 flex flex-col overflow-hidden min-w-0 min-h-0">
          <div className="px-4 py-3 border-b border-outline-variant bg-surface-container/30 shrink-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              {synthesisControls}
              {batchControls}
              {singleBucketControls}
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          {!selected ? (
            <EmptyState
              className="flex-1"
              title="Select a vendor cluster"
              description="Choose a target + contractor from the list. Use Process bucket to run the PDR over all evidence sources and discover or update projects."
            >
              <Icon name="inbox" className="text-5xl text-outline-variant mb-4" />
            </EmptyState>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden min-h-0">
              <div className="p-4 border-b border-outline-variant shrink-0">
                <div className="min-w-0">
                  <FieldLabel className="font-data-mono normal-case">
                    Target · {bucketTarget(selected)}
                  </FieldLabel>
                  <h2 className="font-headline-md text-headline-md flex items-center gap-2 mt-1">
                    <Icon name="hub" className="text-primary" />
                    {bucketContractor(selected)}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <p className="text-body-sm font-data-mono text-on-surface-variant">
                      {selectedTotal} source(s)
                      {Number(selected.lead_source_count || 0) > 0
                        ? ` (${selected.lead_source_count} first found by lead research${selected.source_origin === "lead_research" ? ": second iteration" : ""})`
                        : ""}{" "}
                      · {selectedPending} pending PDR inbox
                    </p>
                    {seedUnresolved.length ? (
                      <Badge
                        tone="error"
                        title="Sources the router held instead of routing — see the routing review queue below"
                      >
                        {seedUnresolved.length} held for review
                      </Badge>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
                <FieldLabel className="mb-4">
                  Projects ({((selected.projects as unknown[]) || []).length})
                </FieldLabel>
                <ul className="space-y-2 mb-6">
                  {(
                    (selected.projects as {
                      project_name?: string;
                      project_id?: string;
                    }[]) || []
                  ).map((p) => (
                    <ProjectParticipationRow
                      key={p.project_id}
                      projectId={String(p.project_id || "")}
                      projectName={String(p.project_name || p.project_id || "")}
                      foundViaLead={
                        isFoundViaLead(leadSummaries[String(p.project_id || "")])
                          ? {
                              lead: leadSummaries[String(p.project_id || "")]?.origin_lead || "",
                              originProject: leadSummaries[String(p.project_id || "")]?.origin_project_name || "",
                            }
                          : null
                      }
                    />
                  ))}
                  {!((selected.projects as unknown[]) || []).length && (
                    <li className="text-body-sm text-on-surface-variant">
                      No projects yet — process this bucket to discover projects via PDR.
                    </li>
                  )}
                </ul>
                {(selected.pipeline as { phase2_url?: string })?.phase2_url ? (
                  <Link
                    to={(selected.pipeline as { phase2_url: string }).phase2_url}
                    className="text-primary text-body-sm hover:underline"
                  >
                    Open Phase 2 evidence for this target
                  </Link>
                ) : null}

                <div className="mt-6">
                  <ClusterWorkbench
                    key={selectedClusterId || selected.bucket_id || "no-cluster"}
                    clusterId={selectedClusterId}
                    targetName={bucketTarget(selected)}
                    seedUnresolved={seedUnresolved}
                  />
                </div>
              </div>
            </div>
          )}
          </div>
        </section>
      </div>

      <div className="shrink-0 flex flex-col border-t border-outline-variant bg-surface-container shadow-[0_-2px_16px_rgba(0,0,0,0.2)]">
        {highlightRun && hasActiveRuns ? (
          <Phase3ProgressDock
            runId={highlightRun.runId}
            targetName={highlightRun.targetName}
            contractorName={highlightRun.contractorName}
            jobStatus={highlightRun.status}
            progress={highlightRun.progress}
            error={highlightRun.error}
            expanded={dockExpanded}
            onToggle={() => setDockExpanded((e) => !e)}
            canStop={["pending", "running"].includes(highlightRun.status)}
            stopping={stoppingRun}
            onStop={() => void stopHighlightedRun()}
            embedded
          >
            {dockExpanded ? (
              <div className="max-h-52 flex flex-col px-3 py-2 overflow-hidden">
                <Phase3SynthesisProgress
                  runId={highlightRun.runId}
                  targetName={highlightRun.targetName}
                  contractorName={highlightRun.contractorName}
                  jobStatus={highlightRun.status}
                  progress={highlightRun.progress}
                  log={highlightRun.log}
                  error={highlightRun.error}
                  compact
                  className="min-h-0 border-0 shadow-none"
                />
              </div>
            ) : null}
          </Phase3ProgressDock>
        ) : null}
      </div>
    </main>
  );
}
